import { useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, functions } from '../../../config/firebase';
import logger from '../../../utils/logger';
import { getCommentRecordId, getReplyRecordId } from '../../../utils/commentIds';
import { useToast } from '../../../components/Toast';

/**
 * Comment & reply CRUD handlers plus post-write notification dispatch.
 * All four flows (add/delete comment, add/delete reply) share the same
 * optimistic-then-refresh pattern:
 *
 *   1. mutate songDetails locally for instant UI feedback
 *   2. persist to Firestore in the background (via SongContext callables or
 *      direct updateDoc for reply deletion, which has no dedicated callable)
 *   3. fire commentNotification / replyNotification / mentionNotification
 *      after the write succeeds, without blocking the UI
 *
 * On add-path failures the optimistic row is rolled back so the UI stays
 * honest.
 */
const useSongCommentActions = ({
  songDetails,
  setSongDetails,
  currentUser,
  addComment,
  deleteComment,
  addReply,
  getSongById,
}) => {
  const { showToast } = useToast();

  const handleAddComment = useCallback(
    (songId, newComment) => {
      if (
        !currentUser ||
        !newComment ||
        newComment.text?.replace(/<[^>]*>/g, '').trim() === ''
      ) {
        return;
      }

      const commentId = String(
        newComment.commentId || newComment.id || Date.now()
      );
      const optimisticComment = {
        id: commentId,
        userId: newComment.userId,
        userName: newComment.displayName,
        commentText: newComment.text,
        mentionedHandles: newComment.mentionedHandles || [],
        timestamp: Timestamp.now(),
        likes: [],
        replies: [],
      };

      setSongDetails((prev) => ({
        ...prev,
        comments: [...(prev.comments || []), optimisticComment],
      }));

      const payload = { ...newComment, commentId, id: commentId };

      void (async () => {
        try {
          const savedCommentId = await addComment(songId, payload);

          try {
            await httpsCallable(functions, 'commentNotification')({
              songId,
              commentText: newComment.text,
              commentId: savedCommentId || null,
            });
          } catch (error) {
            logger.error('Error sending comment notification:', error);
          }

          if (newComment.mentionedHandles?.length > 0) {
            try {
              await httpsCallable(functions, 'mentionNotification')({
                text: newComment.text,
                mentionedHandles: newComment.mentionedHandles,
                songId,
                commentId: savedCommentId || null,
                mentionContext: 'comment',
              });
            } catch (error) {
              logger.error('Error sending mention notification:', error);
            }
          }
        } catch (error) {
          logger.error('Error adding comment:', error);
          setSongDetails((prev) => ({
            ...prev,
            comments: (prev.comments || []).filter(
              (c) => String(getCommentRecordId(c) || '') !== commentId
            ),
          }));
          showToast("Couldn't post comment — please try again", {
            variant: 'error',
          });
        }
      })();
    },
    [currentUser, addComment, setSongDetails, showToast]
  );

  const handleDeleteComment = useCallback(
    async (commentId, commentIndex = null) => {
      try {
        logger.debug('Attempting to delete comment', {
          commentId,
          commentIndex,
        });
        await deleteComment(songDetails.id, commentId, commentIndex);
        const updatedSong = await getSongById(songDetails.id);
        if (updatedSong) {
          setSongDetails(updatedSong);
          logger.debug('Comment deleted successfully:', {
            commentId,
            commentIndex,
          });
        } else {
          logger.warn('Song data unavailable after deleting comment');
        }
      } catch (error) {
        logger.error('Error deleting comment:', error);
      }
    },
    [deleteComment, songDetails.id, getSongById, setSongDetails]
  );

  const handleDeleteReply = useCallback(
    async (commentId, replyIndex, commentIndex = null) => {
      try {
        const songRef = doc(db, 'songs', songDetails.id);
        const targetId =
          commentId !== null && commentId !== undefined
            ? String(commentId)
            : null;

        const currentComments = Array.isArray(songDetails.comments)
          ? songDetails.comments
          : [];

        const updatedComments = currentComments.map((comment, index) => {
          const matchesById =
            targetId !== null && getCommentRecordId(comment) !== null
              ? String(getCommentRecordId(comment)) === targetId
              : false;
          const matchesByIndex =
            commentIndex !== null && commentIndex !== undefined
              ? index === commentIndex
              : false;

          if (matchesById || matchesByIndex) {
            const updatedReplies = (comment.replies || []).filter(
              (_, idx) => idx !== replyIndex
            );
            return { ...comment, replies: updatedReplies };
          }
          return comment;
        });

        await updateDoc(songRef, { comments: updatedComments });
        setSongDetails((prevDetails) => ({
          ...prevDetails,
          comments: updatedComments,
        }));
      } catch (error) {
        logger.error('Error deleting reply:', error);
      }
    },
    [songDetails.id, songDetails.comments, setSongDetails]
  );

  const handleAddReply = useCallback(
    (songId, commentId, newReply, commentIndex = null) => {
      if (
        !currentUser ||
        !newReply ||
        newReply.text?.replace(/<[^>]*>/g, '').trim() === ''
      ) {
        logger.error('Invalid reply attempt:', {
          currentUser: !!currentUser,
          newReply: !!newReply,
          replyText: newReply ? newReply.text : null,
        });
        return;
      }

      if (commentId == null && commentIndex == null) {
        logger.warn('Unable to add reply: missing comment identifier');
        return;
      }

      const targetCommentId =
        commentId != null ? String(commentId) : null;
      const replyId = String(
        newReply.replyId || newReply.commentId || newReply.id || Date.now()
      );
      const optimisticReply = {
        id: replyId,
        userId: currentUser.uid,
        userName: currentUser.displayName,
        commentText: newReply.text,
        mentionedHandles: newReply.mentionedHandles || [],
        timestamp: Timestamp.now(),
        likes: [],
      };

      setSongDetails((prev) => ({
        ...prev,
        comments: (prev.comments || []).map((comment, index) => {
          const cid = getCommentRecordId(comment);
          const matchesById =
            targetCommentId != null &&
            cid != null &&
            String(cid) === targetCommentId;
          const matchesByIndex =
            commentIndex != null && index === commentIndex;
          if (matchesById || matchesByIndex) {
            return {
              ...comment,
              replies: [...(comment.replies || []), optimisticReply],
            };
          }
          return comment;
        }),
      }));

      const rollbackOptimisticReply = () => {
        setSongDetails((prev) => ({
          ...prev,
          comments: (prev.comments || []).map((comment, index) => {
            const cid = getCommentRecordId(comment);
            const matchesById =
              targetCommentId != null &&
              cid != null &&
              String(cid) === targetCommentId;
            const matchesByIndex =
              commentIndex != null && index === commentIndex;
            if (matchesById || matchesByIndex) {
              return {
                ...comment,
                replies: (comment.replies || []).filter(
                  (r) => String(getReplyRecordId(r) || '') !== replyId
                ),
              };
            }
            return comment;
          }),
        }));
      };

      void (async () => {
        let newReplyId;
        try {
          newReplyId = await addReply(
            songId,
            commentId,
            {
              ...newReply,
              userId: currentUser.uid,
              displayName: currentUser.displayName,
              text: newReply.text,
              mentionedHandles: newReply.mentionedHandles || [],
              replyId,
              commentId: replyId,
              id: replyId,
            },
            commentIndex
          );
        } catch (error) {
          logger.error('Error in handleAddReply:', error);
          rollbackOptimisticReply();
          showToast("Couldn't post reply — please try again", {
            variant: 'error',
          });
          return;
        }

        try {
          await httpsCallable(functions, 'replyNotification')({
            songId,
            commentId: targetCommentId,
            commentIndex,
            replyText: newReply.text,
            relatedReplyId: newReplyId || null,
          });
        } catch (err) {
          logger.error('Error sending reply notification:', err);
        }

        if (newReply.mentionedHandles?.length > 0) {
          try {
            await httpsCallable(functions, 'mentionNotification')({
              text: newReply.text,
              mentionedHandles: newReply.mentionedHandles,
              songId,
              commentId,
              mentionContext: 'reply',
              relatedReplyId: newReplyId || null,
            });
          } catch (err) {
            logger.error('Error sending mention notification:', err);
          }
        }
      })();
    },
    [currentUser, addReply, setSongDetails, showToast]
  );

  return {
    handleAddComment,
    handleDeleteComment,
    handleAddReply,
    handleDeleteReply,
  };
};

export default useSongCommentActions;
